<template>
  <div class="container-fluid py-4 px-3 px-md-4">
    <!-- Breadcrumb -->
    <div class="breadcrumb text-md px-0 mb-3">
      <p>
        <span class="breadcrumb-item" @click="$router.push('/dashboard')">Inicio</span>
        &rsaquo; <span class="breadcrumb-item" @click="$router.push('/informes')">Informes</span>
        &rsaquo; Métricas de negocio
      </p>
    </div>

    <!-- Filtros -->
    <div class="card ficha-listado mb-4">
      <div class="card-body px-3 px-md-4 pt-3 pb-2">
        <div class="row g-3 align-items-end mb-2">
          <div class="col-12 col-sm-8 col-md-5 col-lg-4">
            <label class="form-label form-label-sm">Tipster</label>
            <select v-model="tipsterSeleccionado" class="form-select form-select-sm q-select" :disabled="cargandoTipsters" @change="tipsterSeleccionado && cargar()">
              <option value="" disabled>Selecciona tipster…</option>
              <option v-for="t in tipsters" :key="t.id" :value="t.id">{{ t.nombre }}</option>
            </select>
          </div>
          <div class="col-12 col-sm-4 col-md-3 col-lg-2 ms-md-auto" v-if="cargando">
            <span class="spinner-border spinner-border-sm me-1"></span><small class="text-muted">Cargando…</small>
          </div>
        </div>

        <!-- Botones de periodo -->
        <div class="periodo-btns mb-2">
          <button
            v-for="p in periodos"
            :key="p.value"
            :class="['btn-periodo', { active: periodo === p.value }]"
            :disabled="!tipsterSeleccionado || cargando"
            @click="cambiarPeriodo(p.value)"
          >
            <i v-if="p.value === 'custom'" class="fas fa-calendar-alt me-1"></i>
            {{ p.label }}
          </button>
        </div>

        <!-- Fechas custom -->
        <div v-if="periodo === 'custom'" class="custom-dates mt-1 mb-2">
          <input type="date" v-model="fechaDesde" class="form-control form-control-sm q-input date-input" :max="fechaHasta || undefined" />
          <span class="sep-dash">—</span>
          <input type="date" v-model="fechaHasta" class="form-control form-control-sm q-input date-input" :min="fechaDesde || undefined" />
          <button class="btn btn-sm btn-primary" :disabled="!fechaDesde || !fechaHasta || cargando" @click="cargar">Aplicar</button>
        </div>
      </div>
    </div>

    <!-- Estado vacío -->
    <div v-if="!datos && !cargando" class="text-center py-5 text-muted">
      <p class="mb-0">Selecciona un tipster y pulsa <strong>Analizar</strong></p>
    </div>

    <!-- Error -->
    <div v-if="error" class="alert alert-danger py-2">{{ error }}</div>

    <!-- Resultados -->
    <template v-if="datos">
      <p class="mb-1 text-muted" style="font-size:12px">
        Tipster: <strong>{{ datos.tipster.nombre }}</strong>
        · <span v-if="dateRange.date_from && dateRange.date_to">{{ dateRange.date_from }} → {{ dateRange.date_to }}</span>
        <span v-else>Histórico completo</span>
        <span class="ms-2 text-warning" style="font-size:11px">MRR y LTV son siempre históricos</span>
      </p>
      <p v-if="datos.periodo_anterior" class="mb-3 text-muted" style="font-size:11px">
        Las flechas comparan con el periodo anterior ({{ datos.periodo_anterior.desde }} → {{ datos.periodo_anterior.hasta }})
      </p>

      <!-- ─── FILA 1: Financiero ─── -->
      <h6 class="metricas-section-title">Financiero</h6>
      <div class="row g-3 mb-4">

        <!-- Ingreso neto -->
        <div class="col-6 col-lg-3">
          <div class="m-card">
            <div class="m-card-label">Ingreso neto</div>
            <div class="m-card-main">
              <span class="m-val m-val--eur">{{ fmt(datos.ingreso_neto.neto_eur) }}</span>
              <span v-if="datos.ingreso_neto.neto_usd > 0" class="m-val m-val--usd">{{ fmtUsd(datos.ingreso_neto.neto_usd) }}</span>
              <span v-if="datos.ingreso_neto.comparativa" :class="deltaCls(datos.ingreso_neto.comparativa)" class="delta-badge">
                {{ deltaSymbol(datos.ingreso_neto.comparativa) }}{{ fmtDeltaPct(datos.ingreso_neto.comparativa) }}
              </span>
            </div>
            <div class="m-card-sub" v-if="datos.ingreso_neto.devoluciones_eur > 0 || datos.ingreso_neto.devoluciones_usd > 0">
              <span v-if="datos.ingreso_neto.devoluciones_eur > 0" class="text-danger">-{{ fmt(datos.ingreso_neto.devoluciones_eur) }} dev.</span>
              <span v-if="datos.ingreso_neto.devoluciones_usd > 0" class="text-danger ms-1">-{{ fmtUsd(datos.ingreso_neto.devoluciones_usd) }} dev.</span>
            </div>
            <div class="m-card-hint">Bruto: {{ fmt(datos.ingreso_neto.ingresos_brutos_eur) }}<span v-if="datos.ingreso_neto.ingresos_brutos_usd > 0"> · {{ fmtUsd(datos.ingreso_neto.ingresos_brutos_usd) }}</span></div>
          </div>
        </div>

        <!-- MRR -->
        <div class="col-6 col-lg-3">
          <div class="m-card">
            <div class="m-card-label">MRR <span class="badge-snapshot">actual</span></div>
            <div class="m-card-main">
              <span class="m-val m-val--eur">{{ fmt(datos.mrr.eur) }}</span>
              <span v-if="datos.mrr.usd > 0" class="m-val m-val--usd">{{ fmtUsd(datos.mrr.usd) }}</span>
            </div>
            <div class="m-card-sub" v-if="datos.mrr.en_riesgo_eur > 0 || datos.mrr.en_riesgo_usd > 0">
              <span class="text-warning">
                Riesgo: {{ fmt(datos.mrr.en_riesgo_eur) }}<span v-if="datos.mrr.en_riesgo_usd > 0"> · {{ fmtUsd(datos.mrr.en_riesgo_usd) }}</span>
              </span>
            </div>
            <div class="m-card-hint">Ingreso recurrente mensual equivalente</div>
          </div>
        </div>

        <!-- LTV -->
        <div class="col-6 col-lg-3">
          <div class="m-card">
            <div class="m-card-label">LTV por cliente <span class="badge-snapshot">histórico</span></div>
            <div class="m-card-main" v-if="datos.ltv.eur || datos.ltv.usd">
              <span v-if="datos.ltv.eur" class="m-val m-val--eur">{{ fmt(datos.ltv.eur.ltv) }}</span>
              <span v-if="datos.ltv.usd" class="m-val m-val--usd">{{ fmtUsd(datos.ltv.usd.ltv) }}</span>
            </div>
            <div class="m-card-main" v-else><span class="m-val m-muted">—</span></div>
            <div class="m-card-hint">
              <span v-if="datos.ltv.eur">{{ datos.ltv.eur.clientes }} clientes EUR</span>
              <span v-if="datos.ltv.usd"> · {{ datos.ltv.usd.clientes }} USD</span>
            </div>
          </div>
        </div>

        <!-- Churn -->
        <div class="col-6 col-lg-3">
          <div class="m-card" :class="datos.churn.porcentaje > 10 ? 'm-card--alerta' : ''">
            <div class="m-card-label">Churn</div>
            <div class="m-card-main">
              <span class="m-val" :style="datos.churn.porcentaje > 10 ? 'color:#e74c3c' : ''">
                {{ datos.churn.porcentaje }}%
              </span>
              <span v-if="datos.churn.comparativa" :class="deltaClsInverted(datos.churn.comparativa)" class="delta-badge">
                {{ deltaSymbol(datos.churn.comparativa) }}{{ fmtDeltaPct(datos.churn.comparativa) }}
              </span>
            </div>
            <div class="m-card-sub">{{ datos.churn.perdidas }} bajas · {{ datos.churn.activas_inicio }} al inicio</div>
            <div class="m-card-hint">Suscriptores perdidos vs. activos al inicio del periodo</div>
          </div>
        </div>
      </div>

      <!-- ─── FILA 2: Clientes ─── -->
      <h6 class="metricas-section-title">Clientes</h6>
      <div class="row g-3 mb-4">

        <!-- Clientes recurrentes -->
        <div class="col-6 col-lg-3">
          <div class="m-card">
            <div class="m-card-label">Clientes recurrentes</div>
            <div class="m-card-main">
              <span class="m-val">{{ datos.clientes_recurrentes.porcentaje }}%</span>
            </div>
            <div class="m-card-sub">{{ datos.clientes_recurrentes.recurrentes }} de {{ datos.clientes_recurrentes.compradores_unicos }}</div>
            <div class="m-card-hint">Compradores con ≥2 pedidos completados</div>
          </div>
        </div>

        <!-- Cross-sell -->
        <div class="col-6 col-lg-3">
          <div class="m-card">
            <div class="m-card-label">Cross-sell</div>
            <div class="m-card-main">
              <span class="m-val">{{ datos.cross_sell.porcentaje }}%</span>
            </div>
            <div class="m-card-sub">{{ datos.cross_sell.clientes_cross_sell }} clientes · {{ datos.cross_sell.productos_medio_cliente }} prod/cl.</div>
            <div class="m-card-hint">Compraron ≥2 productos distintos</div>
          </div>
        </div>

        <!-- Conversión canal → compra -->
        <div class="col-6 col-lg-3">
          <div class="m-card">
            <div class="m-card-label">Conversión canal → compra</div>
            <div class="m-card-main">
              <span class="m-val">{{ datos.conversion_free.porcentaje }}%</span>
              <span v-if="datos.conversion_free.comparativa" :class="deltaCls(datos.conversion_free.comparativa)" class="delta-badge">
                {{ deltaSymbol(datos.conversion_free.comparativa) }}{{ fmtDeltaPct(datos.conversion_free.comparativa) }}
              </span>
            </div>
            <div class="m-card-sub">{{ datos.conversion_free.compradores }} compradores · {{ datos.conversion_free.usuarios_canal }} en canal</div>
            <div class="m-card-hint">Usuarios del canal Telegram que compraron</div>
          </div>
        </div>

        <!-- Suscripciones perdidas -->
        <div class="col-6 col-lg-3">
          <div class="m-card">
            <div class="m-card-label">MRR perdido</div>
            <div class="m-card-main">
              <span v-if="datos.suscripciones_perdidas.mrr_perdido_eur > 0" class="m-val m-val--eur">-{{ fmt(datos.suscripciones_perdidas.mrr_perdido_eur) }}</span>
              <span v-if="datos.suscripciones_perdidas.mrr_perdido_usd > 0" class="m-val m-val--usd">-{{ fmtUsd(datos.suscripciones_perdidas.mrr_perdido_usd) }}</span>
              <span v-if="datos.suscripciones_perdidas.mrr_perdido_eur === 0 && datos.suscripciones_perdidas.mrr_perdido_usd === 0" class="m-val m-muted">—</span>
              <span v-if="datos.suscripciones_perdidas.comparativa" :class="deltaClsInverted(datos.suscripciones_perdidas.comparativa)" class="delta-badge">
                {{ deltaSymbol(datos.suscripciones_perdidas.comparativa) }}{{ fmtDeltaPct(datos.suscripciones_perdidas.comparativa) }}
              </span>
            </div>
            <div class="m-card-sub">{{ datos.suscripciones_perdidas.cantidad }} canceladas/vencidas</div>
            <div class="m-card-hint">Impacto en MRR de las bajas del periodo</div>
          </div>
        </div>
      </div>

      <!-- ─── FILA 3: Comportamiento ─── -->
      <h6 class="metricas-section-title">Comportamiento</h6>
      <div class="row g-3 mb-4">

        <!-- Tiempo hasta compra (wide) -->
        <div class="col-12 col-lg-6">
          <div class="m-card m-card--wide">
            <div class="m-card-label">Tiempo hasta primera compra (canal → compra)</div>
            <template v-if="datos.tiempo_compra.rangos">
              <div class="m-card-main">
                <span class="m-val">{{ datos.tiempo_compra.media_horas ? horasATexto(datos.tiempo_compra.media_horas) : '—' }}</span>
                <span class="m-card-sub ms-2">media · {{ datos.tiempo_compra.total_usuarios }} usuarios</span>
              </div>
              <div class="rangos-bar mt-3">
                <div v-for="(rango, key) in datos.tiempo_compra.rangos" :key="key" class="rango-item">
                  <div class="rango-label">{{ rangoLabel(key) }}</div>
                  <div class="rango-track">
                    <div class="rango-fill" :style="{ width: rango.pct + '%' }"></div>
                  </div>
                  <div class="rango-pct">{{ rango.pct }}% <span class="rango-n">({{ rango.n }})</span></div>
                </div>
              </div>
            </template>
            <div v-else class="m-card-main"><span class="m-val m-muted">Sin datos</span></div>
            <div class="m-card-hint">Solo usuarios con telegram_id en canal free vinculados a pedidos</div>
          </div>
        </div>

        <!-- Vida media suscriptor -->
        <div class="col-6 col-lg-3">
          <div class="m-card">
            <div class="m-card-label">Vida media suscriptor</div>
            <div class="m-card-main">
              <span v-if="datos.vida_suscriptor.media_meses > 0" class="m-val">{{ datos.vida_suscriptor.media_meses }} meses</span>
              <span v-else class="m-val m-muted">—</span>
            </div>
            <div class="m-card-sub" v-if="datos.vida_suscriptor.total_finalizadas > 0">
              {{ datos.vida_suscriptor.total_finalizadas }} finalizadas · activas {{ datos.vida_suscriptor.activas_antiguedad_dias }}d media
            </div>
            <div class="m-card-hint">Días activa hasta baja (histórico)</div>
          </div>
        </div>

        <!-- Tasa de renovación -->
        <div class="col-6 col-lg-3">
          <div class="m-card" :class="{ 'm-card--empty': datos.tasa_renovacion.total_intentos === 0 }">
            <div class="m-card-label">Tasa de renovación</div>
            <div class="m-card-main" v-if="datos.tasa_renovacion.total_intentos > 0">
              <span class="m-val" :style="tasaRenColor">{{ datos.tasa_renovacion.porcentaje }}%</span>
              <span v-if="datos.tasa_renovacion.comparativa" :class="deltaCls(datos.tasa_renovacion.comparativa)" class="delta-badge">
                {{ deltaSymbol(datos.tasa_renovacion.comparativa) }}{{ fmtDeltaPct(datos.tasa_renovacion.comparativa) }}
              </span>
            </div>
            <div class="m-card-main" v-else><span class="m-val m-muted">—</span></div>
            <div class="m-card-sub" v-if="datos.tasa_renovacion.total_intentos > 0">
              {{ datos.tasa_renovacion.renovaciones_exitosas }} ok · {{ datos.tasa_renovacion.pagos_fallidos }} fallidos
            </div>
            <div class="m-card-hint">{{ datos.tasa_renovacion.total_intentos === 0 ? 'Se irá completando conforme lleguen webhooks' : 'Éxito en renovaciones automáticas Stripe' }}</div>
          </div>
        </div>
      </div>

      <!-- ─── FILA 4: Retención ─── -->
      <h6 class="metricas-section-title">Retención</h6>
      <div class="row g-3 mb-4">

        <!-- Recuperación de pagos -->
        <div class="col-12 col-sm-6 col-lg-4">
          <div class="m-card" :class="{ 'm-card--empty': datos.recuperacion_pagos.pagos_fallidos === 0 }">
            <div class="m-card-label">Recuperación de pagos</div>
            <div class="m-card-main" v-if="datos.recuperacion_pagos.pagos_fallidos > 0">
              <span class="m-val">{{ datos.recuperacion_pagos.porcentaje }}%</span>
              <span v-if="datos.recuperacion_pagos.comparativa" :class="deltaCls(datos.recuperacion_pagos.comparativa)" class="delta-badge">
                {{ deltaSymbol(datos.recuperacion_pagos.comparativa) }}{{ fmtDeltaPct(datos.recuperacion_pagos.comparativa) }}
              </span>
            </div>
            <div class="m-card-main" v-else><span class="m-val m-muted">—</span></div>
            <div class="m-card-sub" v-if="datos.recuperacion_pagos.pagos_fallidos > 0">
              {{ datos.recuperacion_pagos.pagos_recuperados }} / {{ datos.recuperacion_pagos.pagos_fallidos }} pagos fallidos
              <span v-if="datos.recuperacion_pagos.monto_recuperado_eur > 0"> · {{ fmt(datos.recuperacion_pagos.monto_recuperado_eur) }} recuperado</span>
            </div>
            <div class="m-card-hint">{{ datos.recuperacion_pagos.pagos_fallidos === 0 ? 'Se irá completando conforme lleguen webhooks' : 'Pagos fallidos que el cliente terminó pagando' }}</div>
          </div>
        </div>

      </div>
    </template>
  </div>
</template>

<script>
import api from "@/services/apiClient";
const API_URL = process.env.VUE_APP_API_BASE_URL + "/api/v2/";

export default {
  name: "MetricasNegocio",
  data() {
    return {
      tipsters: [],
      tipsterSeleccionado: "",
      periodo: "mes",
      periodos: [
        { value: "all",    label: "Todo" },
        { value: "mes",    label: "Mes actual" },
        { value: "3",      label: "3 meses" },
        { value: "6",      label: "6 meses" },
        { value: "12",     label: "12 meses" },
        { value: "custom", label: "Personalizado" },
      ],
      fechaDesde: "",
      fechaHasta: "",
      datos: null,
      cargando: false,
      cargandoTipsters: true,
      error: null,
    };
  },
  computed: {
    dateRange() {
      const hoy = new Date();
      const fmt = d => d.toISOString().split("T")[0];
      if (this.periodo === "all") return { date_from: null, date_to: null };
      if (this.periodo === "custom") return { date_from: this.fechaDesde || null, date_to: this.fechaHasta || null };
      if (this.periodo === "mes") {
        return { date_from: fmt(new Date(hoy.getFullYear(), hoy.getMonth(), 1)), date_to: fmt(hoy) };
      }
      const meses = parseInt(this.periodo);
      const desde = new Date(hoy);
      desde.setMonth(desde.getMonth() - meses);
      return { date_from: fmt(desde), date_to: fmt(hoy) };
    },
    tasaRenColor() {
      const pct = this.datos?.tasa_renovacion?.porcentaje;
      if (pct === null || pct === undefined) return "";
      if (pct >= 85) return "color:#27ae60";
      if (pct >= 65) return "color:#f39c12";
      return "color:#e74c3c";
    },
  },
  async mounted() {
    await this.cargarTipsters();
    // Pre-rellenar desde el hub de Informes si viene con query params
    const q = this.$route.query;
    if (q.tipster_id) {
      this.tipsterSeleccionado = parseInt(q.tipster_id);
      if (q.desde && q.hasta) {
        this.periodo = "custom";
        this.fechaDesde = q.desde;
        this.fechaHasta = q.hasta;
      } else if (q.periodo && q.periodo !== "custom") {
        this.periodo = q.periodo === "hoy" ? "custom" : q.periodo;
        if (q.periodo === "hoy") {
          const hoy = new Date().toISOString().slice(0, 10);
          this.fechaDesde = hoy;
          this.fechaHasta = hoy;
        }
      }
      this.cargar();
    }
  },
  methods: {
    async cargarTipsters() {
      this.cargandoTipsters = true;
      try {
        const { data } = await api.post(API_URL + "listaTipsters", null, { _silent403: true });
        this.tipsters = Array.isArray(data) ? data : [];
      } catch {
        this.tipsters = [];
      } finally {
        this.cargandoTipsters = false;
      }
    },
    cambiarPeriodo(p) {
      this.periodo = p;
      if (p !== "custom" && this.tipsterSeleccionado) this.cargar();
    },
    async cargar() {
      if (!this.tipsterSeleccionado) return;
      if (this.periodo === "custom" && (!this.fechaDesde || !this.fechaHasta)) return;
      this.cargando = true;
      this.error = null;
      this.datos = null;
      try {
        const { data } = await api.post(API_URL + `metricas-negocio/${this.tipsterSeleccionado}`, {
          dateFrom: this.dateRange.date_from,
          dateTo:   this.dateRange.date_to,
        });
        this.datos = data;
      } catch (e) {
        this.error = e?.response?.data?.error || "Error al cargar las métricas.";
      } finally {
        this.cargando = false;
      }
    },
    fmt(n) {
      return Number(n || 0).toLocaleString("es-ES", { style: "currency", currency: "EUR" });
    },
    fmtUsd(n) {
      return Number(n || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });
    },
    horasATexto(h) {
      if (h < 24) return `${h} h`;
      return `${(h / 24).toFixed(1)} días`;
    },
    rangoLabel(key) {
      const map = { menos_24h: "< 24 h", "1_3_dias": "1–3 d", "4_7_dias": "4–7 d", "8_30_dias": "8–30 d", mas_30dias: "> 30 d" };
      return map[key] || key;
    },
    // Comparativa helpers
    deltaSymbol(c) {
      if (!c) return "";
      if (c.tendencia === "sube") return "↑";
      if (c.tendencia === "baja") return "↓";
      return "→";
    },
    fmtDeltaPct(c) {
      if (!c || c.variacion_pct === null) return "";
      return Math.abs(c.variacion_pct) + "%";
    },
    deltaCls(c) {
      if (!c) return "";
      if (c.tendencia === "sube") return "delta-badge--up";
      if (c.tendencia === "baja") return "delta-badge--down";
      return "delta-badge--flat";
    },
    // Para churn y MRR perdido: subir es malo
    deltaClsInverted(c) {
      if (!c) return "";
      if (c.tendencia === "sube") return "delta-badge--down";
      if (c.tendencia === "baja") return "delta-badge--up";
      return "delta-badge--flat";
    },
  },
};
</script>

<style scoped>
.page-title { font-size: 20px; font-weight: 700; color: var(--q-text); }

.metricas-section-title {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: .08em;
  text-transform: uppercase;
  color: var(--q-muted);
  margin-bottom: 10px;
  padding-left: 2px;
}

/* ── Cards ── */
.m-card {
  background: var(--q-card);
  border: 1px solid var(--q-border);
  border-radius: 12px;
  padding: 12px 14px;
  min-height: 100px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  height: 100%;
}
.m-card--wide { min-height: 140px; }
.m-card--empty {
  opacity: .6;
  border-style: dashed;
}
.m-card--alerta {
  border-color: rgba(231,76,60,.35);
  background: rgba(231,76,60,.05);
}

.m-card-label {
  font-size: 11px;
  font-weight: 600;
  opacity: .7;
  line-height: 1.25;
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.m-card-main {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 8px;
  margin-top: 4px;
}
.m-val {
  font-size: 18px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: var(--q-text);
}
@media (min-width: 768px) {
  .m-val { font-size: 20px; }
}
.m-val--eur { color: var(--q-gold, #d4af37); }
.m-val--usd { color: #26c6da; }
.m-muted { opacity: .4; font-size: 16px; }

.m-card-sub {
  font-size: 12px;
  opacity: .8;
  line-height: 1.3;
}
.m-card-hint {
  font-size: 11px;
  opacity: .5;
  margin-top: auto;
  padding-top: 6px;
  line-height: 1.3;
}

/* Badge "actual" / "histórico" */
.badge-snapshot {
  font-size: 9px;
  font-weight: 700;
  letter-spacing: .05em;
  text-transform: uppercase;
  background: rgba(212,175,55,.15);
  color: var(--q-gold, #d4af37);
  border-radius: 20px;
  padding: 1px 6px;
}

/* ── Delta badges ── */
.delta-badge {
  font-size: 11px;
  font-weight: 700;
  border-radius: 20px;
  padding: 1px 7px;
  white-space: nowrap;
  align-self: center;
}
.delta-badge--up   { background: rgba(39,174,96,.15);  color: #27ae60; }
.delta-badge--down { background: rgba(231,76,60,.15);  color: #e74c3c; }
.delta-badge--flat { background: rgba(150,150,150,.1); color: var(--q-muted, #888); }

/* ── Rangos de tiempo ── */
.rangos-bar { display: flex; flex-direction: column; gap: 6px; }
.rango-item {
  display: grid;
  grid-template-columns: 55px 1fr 60px;
  align-items: center;
  gap: 6px;
}
@media (min-width: 576px) {
  .rango-item { grid-template-columns: 65px 1fr 70px; }
}
.rango-label { font-size: 11px; opacity: .7; text-align: right; white-space: nowrap; }
.rango-track {
  height: 6px;
  background: var(--q-border);
  border-radius: 3px;
  overflow: hidden;
  min-width: 0;
}
.rango-fill {
  height: 100%;
  background: var(--q-gold, #d4af37);
  border-radius: 3px;
  transition: width .4s ease;
  min-width: 2px;
}
.rango-pct { font-size: 11px; font-weight: 600; font-variant-numeric: tabular-nums; white-space: nowrap; }
.rango-n { font-weight: 400; opacity: .6; }

/* Select / Input */
.q-select, .q-input {
  background: var(--q-card);
  border-color: var(--q-border);
  color: var(--q-text);
}
.q-select:focus, .q-input:focus {
  background: var(--q-card);
  color: var(--q-text);
  border-color: var(--q-gold, #d4af37);
  box-shadow: none;
}

/* ── Botones de periodo ── */
.periodo-btns { display: flex; gap: 6px; flex-wrap: wrap; }
.btn-periodo {
  padding: 4px 12px;
  font-size: .8rem;
  font-weight: 500;
  border: 1px solid var(--q-border);
  border-radius: 20px;
  background: transparent;
  color: var(--q-muted, #888);
  cursor: pointer;
  transition: border-color .15s, color .15s, background .15s;
  white-space: nowrap;
}
.btn-periodo:hover:not(:disabled) {
  border-color: var(--q-gold, #d4af37);
  color: var(--q-text);
}
.btn-periodo.active {
  background: var(--q-gold, #d4af37);
  border-color: var(--q-gold, #d4af37);
  color: #000;
  font-weight: 700;
}
.btn-periodo:disabled { opacity: .45; cursor: not-allowed; }

/* Fechas custom */
.custom-dates {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.date-input { max-width: 150px; }
.sep-dash { opacity: .5; font-size: 14px; }

@media (max-width: 575px) {
  .btn-periodo { font-size: .75rem; padding: 4px 10px; }
  .date-input  { max-width: 130px; }
}

/* Responsive: cards más compactas en xs */
@media (max-width: 575px) {
  .m-card { padding: 10px 12px; min-height: 90px; }
  .m-val  { font-size: 16px; }
}
</style>
